import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, ShieldCheck, Download, Award } from 'lucide-react';
import { useCart } from '../context/CartContext';
const UPSELLS = [{
  id: 'upsell-1',
  name: 'Plan Fit 21 Días: Comé Sano, Perdé Grasa y Ganá Músculo con tu Air Fryer',
  description: '21 días de menú fit con macros incluidos',
  originalPrice: 19990,
  offerPrice: 3990,
  image: 'https://cdn.magicpatterns.com/uploads/qmZmxC7KbdsGXzVtC8JHgG/plan-fit-21-dias-mockup3d.png'
}, {
  id: 'upsell-2',
  name: 'Air Fryer Kids: 20 Recetas que los Chicos Piden Siempre',
  description: 'Nuggets, pizzetas y postres sin aceite para toda la familia',
  originalPrice: 19990,
  offerPrice: 3990,
  image: 'https://cdn.magicpatterns.com/uploads/8hPwQaGiQbijJtgGA2eewB/air-fryer-kids-mockup3d.png'
}, {
  id: 'upsell-3',
  name: 'Recetas para Agasajar: 10 Platos Imperdibles para Reuniones y Asados',
  description: 'Impresioná a tus invitados con platos listos en minutos con tu freidora',
  originalPrice: 19990,
  offerPrice: 3990,
  image: 'https://cdn.magicpatterns.com/uploads/dQsYDqUWcpFbS8twwkRjB9/recetas-para-agasajar-mockup3d.png'
}];
const MAIN_PRODUCT_PRICE = 14990;
const MAIN_PRODUCT_ORIGINAL = 29980;
export function CheckoutCart() {
  const {
    isCartOpen,
    setCartOpen
  } = useCart();
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [selectedUpsells, setSelectedUpsells] = useState<Set<string>>(new Set());
  // Lock body scroll when cart is open
  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isCartOpen]);
  // Timer logic
  useEffect(() => {
    const storedTime = sessionStorage.getItem('cartTimer');
    const startTime = storedTime ? parseInt(storedTime, 10) : Date.now();
    if (!storedTime) {
      sessionStorage.setItem('cartTimer', startTime.toString());
    }
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000);
      const remaining = Math.max(0, 600 - elapsed);
      setTimeLeft(remaining);
      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(price);
  };
  const toggleUpsell = (id: string) => {
    const newSelected = new Set(selectedUpsells);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedUpsells(newSelected);
  };
  // Calculations
  const subtotal = MAIN_PRODUCT_PRICE + Array.from(selectedUpsells).reduce((acc, id) => {
    const upsell = UPSELLS.find(u => u.id === id);
    return acc + (upsell?.offerPrice || 0);
  }, 0);
  const totalDiscount = MAIN_PRODUCT_ORIGINAL - MAIN_PRODUCT_PRICE + Array.from(selectedUpsells).reduce((acc, id) => {
    const upsell = UPSELLS.find(u => u.id === id);
    return acc + ((upsell?.originalPrice || 0) - (upsell?.offerPrice || 0));
  }, 0);
  return <AnimatePresence data-id="element-47">
      {isCartOpen && <>
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={() => setCartOpen(false)} className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm" data-id="element-48" />

          {/* Cart Panel */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed top-0 right-0 h-[100dvh] w-full md:w-[420px] bg-white z-50 flex flex-col shadow-2xl" data-id="element-49">
            {/* Header */}
            <div className="bg-[#1A1A1A] text-white pt-4 pb-3 px-4 relative flex-shrink-0" data-id="element-50">
              <h2 className="text-center font-bold text-base" data-id="element-51">Tu Carrito</h2>
              <button onClick={() => setCartOpen(false)} className="absolute right-4 top-4 text-white/80 hover:text-white transition-colors" data-id="element-52">
                <X className="w-5 h-5" data-id="element-53" />
              </button>

              {/* Timer Bar */}
              <div className="mt-3 bg-primary rounded-lg py-2 px-3 flex items-center justify-center gap-2" data-id="element-54">
                <span className="text-sm font-medium" data-id="element-55">
                  {timeLeft > 0 ? 'No pierdas el descuento' : 'Oferta expirada'}
                </span>
                {timeLeft > 0 && <span className="font-bold text-sm bg-black/20 px-2 py-0.5 rounded" data-id="element-56">
                    {formatTime(timeLeft)}
                  </span>}
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto pb-6" data-id="element-57">
              {/* Main Product */}
              <div className="p-4 border-b border-gray-100" data-id="element-58">
                <div className="flex gap-4" data-id="element-59">
                  <div className="w-[60px] h-[60px] rounded-lg flex-shrink-0 overflow-hidden bg-[#FAECE7]" data-id="element-60">
                    <img src="https://cdn.magicpatterns.com/uploads/fkWik1NENfN7RXT68SnTZw/recetas-air-fryer-argentinas-portada_mockup3d_sinfondo.png" alt="Recetas Air Fryer Argentinas" className="w-full h-full object-cover" data-id="element-61" />
                  </div>
                  <div className="flex-1" data-id="element-62">
                    <h3 className="font-bold text-[#3A2B24] text-sm leading-tight mb-0.5" data-id="element-63">
                      Recetas Air Fryer Argentinas
                    </h3>
                    <p className="text-[11px] text-[#1D9E75] font-semibold mb-2" data-id="element-64">
                      🎁 +3 eBook GRATIS
                    </p>
                    <div className="flex items-center gap-3 mb-2" data-id="element-65">
                      <div className="flex items-center border border-gray-200 rounded-md" data-id="element-66">
                        <button disabled className="px-2 py-1 text-gray-400 cursor-not-allowed text-sm font-medium" data-id="element-67">
                          −
                        </button>
                        <span className="px-2 py-1 text-sm font-medium" data-id="element-68">1</span>
                        <button disabled className="px-2 py-1 text-gray-400 cursor-not-allowed text-sm font-medium" data-id="element-69">
                          +
                        </button>
                      </div>
                      <div className="flex flex-col items-end flex-1" data-id="element-70">
                        <span className="text-xs text-gray-400 line-through" data-id="element-71">
                          {formatPrice(MAIN_PRODUCT_ORIGINAL)}
                        </span>
                        <span className="font-bold text-primary" data-id="element-72">
                          {formatPrice(MAIN_PRODUCT_PRICE)}
                        </span>
                      </div>
                    </div>
                    {timeLeft > 0 && <span className="inline-block bg-[#1D9E75] text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-73">
                        Ahorrás 50%
                      </span>}
                  </div>
                </div>
              </div>

              {/* Discount Banner */}
              {timeLeft > 0 && <div className="bg-primary/10 py-3 px-4 text-center border-y border-primary/20" data-id="element-74">
                  <p className="font-bold text-primary text-sm" data-id="element-75">
                    SOLO POR HOY -50%
                  </p>
                  <p className="text-xs text-primary/80 italic mt-0.5" data-id="element-76">
                    Oferta válida mientras el contador esté activo
                  </p>
                </div>}

              {/* Upsells */}
              <div className="p-4 bg-gray-50/50" data-id="element-77">
                <h4 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-4" data-id="element-78">
                  Clientes que compraron esto también llevaron:
                </h4>

                <div className="space-y-3" data-id="element-79">
                  {UPSELLS.map(upsell => {
                const isSelected = selectedUpsells.has(upsell.id);
                return <div key={upsell.id} className={`bg-white border rounded-xl p-3 flex gap-3 items-center transition-all duration-200 ${isSelected ? 'border-[#1D9E75] shadow-sm' : 'border-gray-200'}`} data-id="element-80">
                        <div className="w-[50px] h-[50px] rounded-lg flex-shrink-0 overflow-hidden bg-gray-100" data-id="element-81">
                          <img src={upsell.image} alt={upsell.name} className="w-full h-full object-cover" data-id="element-82" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-83">
                          <h5 className="font-bold text-[#3A2B24] text-[13px] leading-tight line-clamp-2" data-id="element-84">
                            {upsell.name}
                          </h5>
                          <p className="text-[11px] text-gray-500 line-clamp-2 mb-1" data-id="element-85">
                            {upsell.description}
                          </p>
                          <div className="flex items-baseline gap-2" data-id="element-86">
                            <span className="font-bold text-primary text-sm" data-id="element-87">
                              +{formatPrice(upsell.offerPrice)}
                            </span>
                            <span className="text-[10px] text-gray-400 line-through" data-id="element-88">
                              {formatPrice(upsell.originalPrice)}
                            </span>
                          </div>
                        </div>
                        <button onClick={() => toggleUpsell(upsell.id)} className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-[#1D9E75] text-white' : 'bg-primary/10 text-primary hover:bg-primary hover:text-white'}`} data-id="element-89">
                          {isSelected ? <Check className="w-4 h-4" data-id="element-90" /> : <span className="text-lg leading-none font-medium" data-id="element-91">
                              +
                            </span>}
                        </button>
                      </div>;
              })}
                </div>
              </div>
            </div>

            {/* Sticky Bottom Summary */}
            <div className="border-t border-gray-200 bg-white p-4 flex-shrink-0 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]" data-id="element-92">
              <div className="space-y-2 mb-4" data-id="element-93">
                <div className="flex justify-between items-center text-sm" data-id="element-94">
                  <span className="text-gray-500" data-id="element-95">Descuento total</span>
                  <motion.span key={totalDiscount} initial={{
                scale: 1.1,
                color: '#1D9E75'
              }} animate={{
                scale: 1,
                color: '#1D9E75'
              }} className="font-medium text-[#1D9E75]" data-id="element-96">
                    -{formatPrice(totalDiscount)}
                  </motion.span>
                </div>
                <div className="flex justify-between items-center" data-id="element-97">
                  <span className="font-bold text-[#3A2B24]" data-id="element-98">Subtotal</span>
                  <motion.span key={subtotal} initial={{
                scale: 1.1
              }} animate={{
                scale: 1
              }} className="font-bold text-xl text-[#3A2B24]" data-id="element-99">
                    {formatPrice(subtotal)}
                  </motion.span>
                </div>
              </div>

              <button onClick={() => {
            const items = ['46518849142973:1'];
            if (selectedUpsells.has('upsell-1')) items.push('46594449998013:1');
            if (selectedUpsells.has('upsell-2')) items.push('46594463367357:1');
            if (selectedUpsells.has('upsell-3')) items.push('46594465923261:1');
            const url = `https://valordigital.store/cart/${items.join(',')}`;
            window.location.href = url;
          }} className="w-full bg-[#1D9E75] hover:bg-[#168562] text-white font-bold text-lg py-4 rounded-xl shadow-soft transition-colors mb-4" data-id="element-100">
                PAGAR AHORA
              </button>

              <div className="flex justify-center items-center gap-3 mb-4 opacity-60" data-id="element-101">
                <span className="text-xs font-bold" data-id="element-102">MercadoPago</span>
                <span className="text-gray-300" data-id="element-103">•</span>
                <span className="text-xs font-bold" data-id="element-104">Visa</span>
                <span className="text-gray-300" data-id="element-105">•</span>
                <span className="text-xs font-bold" data-id="element-106">Mastercard</span>
                <span className="text-gray-300" data-id="element-107">•</span>
                <span className="text-xs font-bold" data-id="element-108">AMEX</span>
              </div>

              <div className="flex flex-col items-center gap-1.5 text-[10px] text-gray-500" data-id="element-109">
                <div className="flex items-center gap-1" data-id="element-110">
                  <ShieldCheck className="w-3 h-3" data-id="element-111" />
                  <span data-id="element-112">Pago protegido por Mercado Pago</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-113">
                  <Download className="w-3 h-3" data-id="element-114" />
                  <span data-id="element-115">Descarga inmediata al pagar</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-116">
                  <Award className="w-3 h-3" data-id="element-117" />
                  <span data-id="element-118">Garantía de 7 días sin preguntas</span>
                </div>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}